
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Product {
  id: string;
  name: string;
  price: number;
  original_price?: number;
  image_url: string;
  description: string;
  category: string;
  stock: number;
  is_new?: boolean;
  is_on_sale?: boolean;
}

export default function StockManager() {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStock = async (productId: string, newStock: number) => {
    if (newStock < 0) return;
    
    try {
      const { error } = await supabase
        .from('products')
        .update({ stock: newStock })
        .eq('id', productId);

      if (error) throw error;

      // Actualizar el estado local
      const updatedProducts = products.map(product => 
        product.id === productId 
          ? { ...product, stock: newStock }
          : product
      );
      setProducts(updatedProducts);
    } catch (error) {
      console.error('Error updating stock:', error);
      alert('Error al actualizar el stock');
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    const matchesStock = 
      filterStock === 'all' ||
      (filterStock === 'low' && product.stock <= 5) ||
      (filterStock === 'out' && product.stock === 0) ||
      (filterStock === 'available' && product.stock > 0);
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { text: 'Sin stock', color: 'bg-red-100 text-red-800' };
    if (stock <= 5) return { text: 'Stock bajo', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Disponible', color: 'bg-green-100 text-green-800' };
  };

  const lowStockCount = products.filter(p => p.stock <= 5 && p.stock > 0).length;
  const outOfStockCount = products.filter(p => p.stock === 0).length;
  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#C6A45C] animate-spin mb-4"></i>
          <p className="text-gray-400">Cargando productos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
          Control de Stock
        </h2>
        <button 
          onClick={loadProducts}
          className="bg-[#C6A45C] text-black px-4 py-2 rounded-lg hover:bg-[#B8965A] transition-colors font-medium flex items-center space-x-2 whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          <span>Actualizar</span>
        </button>
      </div>

      {/* Stock Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-800">
              <i className="ri-archive-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Total Productos</p>
              <p className="text-2xl font-bold text-white">{products.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-800">
              <i className="ri-alert-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Stock Bajo</p>
              <p className="text-2xl font-bold text-white">{lowStockCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-800">
              <i className="ri-close-circle-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Sin Stock</p>
              <p className="text-2xl font-bold text-white">{outOfStockCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-800">
              <i className="ri-money-dollar-circle-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Valor Total</p>
              <p className="text-2xl font-bold text-white">{formatPrice(totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Buscar producto
            </label>
            <div className="relative">
              <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300"
                placeholder="Nombre del producto..."
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Categoría
            </label>
            <select 
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 pr-8"
            >
              <option value="all">Todas las categorías</option>
              <option value="carteras">Carteras</option>
              <option value="accesorios">Accesorios</option>
            </select>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Estado de stock
            </label>
            <select 
              value={filterStock}
              onChange={(e) => setFilterStock(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 pr-8"
            >
              <option value="all">Todos los estados</option>
              <option value="available">Disponible</option>
              <option value="low">Stock bajo</option>
              <option value="out">Sin stock</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Producto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Precio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Stock Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredProducts.map((product) => {
                const stockStatus = getStockStatus(product.stock);
                return (
                  <tr key={product.id} className="hover:bg-gray-800 transition-colors duration-300">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-lg mr-4"
                        />
                        <div>
                          <div className="text-sm font-medium text-white">{product.name}</div>
                          <div className="text-sm text-gray-400">ID: {product.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">
                      {product.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#C6A45C] font-medium">{formatPrice(product.price)}</div>
                      <div className="text-sm text-gray-400">Valor: {formatPrice(product.price * product.stock)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => updateStock(product.id, product.stock - 1)}
                          className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-all duration-300"
                          disabled={product.stock === 0}
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="text-white font-medium min-w-[3rem] text-center">
                          {product.stock}
                        </span>
                        <button 
                          onClick={() => updateStock(product.id, product.stock + 1)}
                          className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-all duration-300"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="number"
                          min="0"
                          value={product.stock}
                          onChange={(e) => updateStock(product.id, parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:border-[#C6A45C] focus:outline-none"
                        />
                        <button 
                          onClick={() => updateStock(product.id, 0)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-300"
                          title="Marcar como sin stock"
                        >
                          <i className="ri-close-circle-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-search-line text-6xl text-gray-600 mb-4"></i>
          <p className="text-gray-400 text-lg">No se encontraron productos con los filtros aplicados</p>
        </div>
      )}
    </div>
  );
}
